import { Controller } from 'stimulus';
import { FetchRequest } from '@rails/request.js';
import { Meta, useTimeout } from '@/js/util';
import { templateSource } from '@/js/shared/template_source';
import { openSidepanel, resetTemplatesPanel, MY_LIBRARY_SIDEPANEL_ID } from '@/js/shared/sidepanel';

class Template {
  constructor(form) {
    this.form = form;
  }

  get id() {
    return new FormData(this.form).get('id');
  }

  get state() {
    if (this.form.classList.contains('is-disabled')) {
      return 'disabled';
    }

    if (this.form.classList.contains('is-loading')) {
      return 'loading';
    }

    if (this.form.classList.contains('is-added')) {
      return 'added';
    }
  }

  set state(value) {
    this.form.classList.toggle('is-disabled', value === 'disabled');
    this.form.classList.toggle('is-loading', value === 'loading');
    this.form.classList.toggle('is-added', value === 'added');
  }
}

export default class extends Controller {
  static targets = ['template', 'applyAllButton'];

  static values = {
    method: String,
    action: String,
  };

  initialize() {
    this.processingIds = new Set();
    this.processingTimeouts = new Map();
  }

  connect() {
    useTimeout(this);
  }

  templateTargetConnected(element) {
    const template = new Template(element);
    if (template.state === 'added') {
      this.processingIds.delete(template.id);
      this.#clearFallbackTimeout(template.id);
      // TODO: df-templates when applying them all - we need to change state once all are done
      this.timeout(3000, () => {
        template.state = 'default';
      });
    }

    // if done processing all IDs, remove loading state from "insert all" button
    if (this.processingIds.size === 0 && this.hasApplyAllButtonTarget) {
      this.applyAllButtonTarget.classList.remove('is-loading');
    }
  }

  applyAll() {
    if (this.hasApplyAllButtonTarget) {
      this.applyAllButtonTarget.classList.add('is-loading');
    }

    const ids = this.templateTargets.map((target) => {
      const template = new Template(target);
      template.state = 'loading';
      this.processingIds.add(template.id);
      this.#startFallbackTimeout(template.id);
      return template.id;
    });
    const body = this.#prepareBody(ids, 'collection');
    const request = new FetchRequest(this.methodValue, this.actionValue, { body });

    const templateDOMIds = ids.map((id) => `board_list_template_board_${id}`);
    this.dispatch('applied', { detail: { domIds: templateDOMIds } });

    request.perform().catch(console.error);
  }

  /**
   * @param {SubmitEvent} e
   */
  apply({ target }) {
    if (this.#canApplyToCurrentBoard(target)) {
      this.#applyToCurrentBoard(target);
      return;
    }

    if (this.hasApplyAllButtonTarget) {
      this.applyAllButtonTarget.classList.add('is-loading');
    }

    const template = new Template(target);
    template.state = 'loading';
    const id = new Template(target).id;
    this.processingIds.add(id);
    this.#startFallbackTimeout(id);
    const body = this.#prepareBody([id], 'single');
    const request = new FetchRequest(this.methodValue, this.actionValue, { body });

    const templateDOMId = `board_list_template_board_${id}`;
    this.dispatch('applied', { detail: { domIds: [templateDOMId] } });

    request.perform().catch(console.error);
  }

  // An empty board with the blank-canvas hint is filled in place; cross-team templates (e.g. HSR)
  // go through the job instead, which copies their items.
  #canApplyToCurrentBoard(form) {
    return (
      form.dataset.appliesInPlace === 'true' &&
      document.querySelector('#blank_canvas_message[data-can-fill-board]') !== null &&
      document.querySelectorAll('.item').length === 0
    );
  }

  #applyToCurrentBoard(form) {
    // Nothing round-trips to the server here, so guard the pick ourselves: the gate above stays
    // open until the first item renders, which is long enough for a double click to apply twice.
    const template = new Template(form);
    if (template.state === 'loading') {
      return;
    }
    template.state = 'loading';
    this.timeout(3000, () => {
      template.state = 'default';
    });

    const { templateUrl, templateName, collectionName, collectionKind } = form.dataset;

    this.dispatch('applyToCurrentBoard', {
      detail: {
        templateUrl,
        templateName,
        collectionName,
        collectionKind,
        source: templateSource(),
      },
    });

    // The template has done its job; the next step is swapping in their own products, which is also
    // the panel a board with content opens on. See Moodboard::Drawer#default_panel.
    resetTemplatesPanel();
    openSidepanel(MY_LIBRARY_SIDEPANEL_ID);
  }

  #prepareBody(ids, applyMethod) {
    const body = {};
    body['board_id'] = Meta.get('board-id');
    body['template_ids'] = ids;
    body['apply_method'] = applyMethod;
    return body;
  }

  #startFallbackTimeout(id) {
    this.#clearFallbackTimeout(id);
    const timerId = this.timeout(30000, () => {
      this.processingTimeouts.delete(id);
      if (this.processingIds.delete(id)) {
        const target = this.templateTargets.find((el) => new Template(el).id === id);
        if (target) {
          new Template(target).state = 'default';
        }
        if (this.processingIds.size === 0 && this.hasApplyAllButtonTarget) {
          this.applyAllButtonTarget.classList.remove('is-loading');
        }
      }
    });
    this.processingTimeouts.set(id, timerId);
  }

  #clearFallbackTimeout(id) {
    const timerId = this.processingTimeouts.get(id);
    if (timerId !== undefined) {
      this.clearTimeout(timerId);
      this.processingTimeouts.delete(id);
    }
  }
}
